import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  useGetTeamSimulationDetailsQuery,
  useAutoStartTeamSimulationMutation,
} from '../app/services/meseeknaApiV2'
import {
  DevicePreview,
  CountdownTimer,
  ParticipantWaitList,
  WaitingParticipant,
} from '../features/collaboration/components/WaitingRoom'
import LogoFull from '../components/logo-full'

const CollaborativeSimulationSimple: React.FC = () => {
  const { teamSimulationId } = useParams()
  const navigate = useNavigate()

  // Track if auto-start has been attempted
  const [autoStartAttempted, setAutoStartAttempted] = useState(false)
  const [autoStartError, setAutoStartError] = useState<string | null>(null)

  // Track if we've ever successfully loaded data - prevents showing error page on transient failures
  const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)

  // Get current user email from localStorage (set during login)
  const currentUserEmail = useMemo(() => {
    // Try to get email from localStorage (set during passcode login)
    const storedEmail = localStorage.getItem('email') || localStorage.getItem('userEmail')
    return storedEmail || ''
  }, [])

  // Check if this is a team simulation
  const isTeamSimulation = useMemo(() => {
    const result =
      !!teamSimulationId && window.location.pathname.includes('/team-simulations/')
    return result
  }, [teamSimulationId])

  // Determine if we should poll based on simulation status
  const [shouldPoll, setShouldPoll] = useState(true)

  // Get team simulation details with polling
  const {
    data: teamSimulationData,
    error: teamSimulationError,
    isLoading: isLoadingTeamSimulation,
    refetch,
  } = useGetTeamSimulationDetailsQuery(
    { teamSimulationId: teamSimulationId! },
    {
      skip: !isTeamSimulation || !teamSimulationId,
      refetchOnMountOrArgChange: true,
      pollingInterval: shouldPoll ? 3000 : 0, // Poll every 3 seconds while waiting
    }
  )

  // Auto-start mutation
  const [autoStartTeamSimulation, { isLoading: isAutoStarting }] =
    useAutoStartTeamSimulationMutation()

  // Extract simulation data
  const simulationData = teamSimulationData?.data
  const simulationStatus = simulationData?.status
  const scheduledStartTime = simulationData?.scheduledStartTime
    ? new Date(simulationData.scheduledStartTime)
    : null

  // Check if simulation is already active (for late joiners)
  const isSimulationActive = simulationStatus === 'active'
  const isSimulationPending = simulationStatus === 'pending'
  const isSimulationEnded =
    simulationStatus === 'completed' || simulationStatus === 'cancelled'

  // Debug logging for waiting room state changes
  useEffect(() => {
    console.log('[WaitingRoom] State:', {
      teamSimulationId,
      status: simulationStatus,
      isActive: isSimulationActive,
      isPending: isSimulationPending,
      isEnded: isSimulationEnded,
      hasError: !!teamSimulationError,
      hasLoadedSuccessfully,
      scheduledStartTime: scheduledStartTime?.toISOString(),
      now: new Date().toISOString(),
    })
  }, [teamSimulationId, simulationStatus, isSimulationActive, isSimulationPending, isSimulationEnded, teamSimulationError, hasLoadedSuccessfully, scheduledStartTime])

  // Map participants to the format expected by ParticipantWaitList
  const participants: WaitingParticipant[] = useMemo(() => {
    if (!simulationData?.participants) return []

    return simulationData.participants.map((p) => ({
      id: String(p.id),
      email: p.email,
      firstName: p.firstName || null,
      lastName: p.lastName || null,
      status: (p.status as WaitingParticipant['status']) || 'invited',
      joinedAt: null,
    }))
  }, [simulationData?.participants])

  // Track successful data loading and handle connection errors gracefully
  useEffect(() => {
    if (teamSimulationData?.data) {
      // Successfully received data - mark as loaded
      if (!hasLoadedSuccessfully) {
        setHasLoadedSuccessfully(true)
      }
      // Clear any previous connection error
      if (connectionError) {
        setConnectionError(null)
      }
    }
  }, [teamSimulationData?.data, hasLoadedSuccessfully, connectionError])

  // Handle API errors - show warning but don't hide waiting room if we have data
  useEffect(() => {
    if (teamSimulationError && hasLoadedSuccessfully) {
      // Transient error - show warning but keep the waiting room visible
      console.error('Waiting room: Transient API error:', teamSimulationError)
      setConnectionError('Connection issue. Trying to reconnect...')
      // The polling will continue and hopefully recover
    }
  }, [teamSimulationError, hasLoadedSuccessfully])

  // Navigate to full simulation when status becomes active
  useEffect(() => {
    if (isSimulationActive) {
      setShouldPoll(false)
      // Small delay to let the UI update
      const timer = setTimeout(() => {
        navigate(`/team-simulations/${teamSimulationId}/simulation-full`)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isSimulationActive, teamSimulationId, navigate])

  // Handle countdown reaching zero - trigger auto-start
  const handleTimeUp = useCallback(async () => {
    if (autoStartAttempted || !teamSimulationId || isAutoStarting) return

    setAutoStartAttempted(true)
    setAutoStartError(null)

    try {
      const result = await autoStartTeamSimulation({ teamSimulationId }).unwrap()

      if (result.status === 'success') {
        // Simulation started - the polling will detect it and navigate
        console.log('Auto-start successful:', result)
      } else if (result.status === 'pending') {
        // Conditions not met yet - will retry via polling
        console.log('Auto-start pending:', result.message)
        setAutoStartError(result.message || 'Waiting for simulation to start...')
        // Reset so we can try again
        setTimeout(() => {
          setAutoStartAttempted(false)
        }, 3000)
      }
    } catch (err) {
      console.error('Auto-start failed:', err)
      setAutoStartError('Failed to start simulation. Retrying...')
      // Reset to allow retry
      setTimeout(() => {
        setAutoStartAttempted(false)
        refetch()
      }, 5000)
    }
  }, [autoStartAttempted, teamSimulationId, isAutoStarting, autoStartTeamSimulation, refetch])

  // Handle retry button
  const handleRetry = useCallback(() => {
    setAutoStartAttempted(false)
    setAutoStartError(null)
    refetch()
  }, [refetch])

  // Loading state - only show if we haven't loaded successfully before
  if (isLoadingTeamSimulation && !hasLoadedSuccessfully) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Loading Team Simulation
          </h2>
          <p className="text-gray-600">Please wait while we load your simulation...</p>
        </div>
      </div>
    )
  }

  // Error state - only show full error page if we've never successfully loaded
  if (teamSimulationError && !hasLoadedSuccessfully) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-lg shadow-md p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Unable to Load Simulation
          </h2>
          <p className="text-gray-600 mb-4">
            There was an error loading your team simulation. Please check that you have
            the correct link and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Simulation ended state
  if (isSimulationEnded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-lg shadow-md p-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Simulation {simulationStatus === 'completed' ? 'Completed' : 'Cancelled'}
          </h2>
          <p className="text-gray-600 mb-4">
            This team simulation has ended. Thank you for participating.
          </p>
        </div>
      </div>
    )
  }

  // No team simulation ID
  if (!teamSimulationId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-lg shadow-md p-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Team Simulation</h2>
          <p className="text-gray-600 mb-4">
            No team simulation ID provided. Please use a valid invitation link.
          </p>
          <a
            href="/dashboard"
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    )
  }

  // Main waiting room UI
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <LogoFull />
            <div className="text-sm text-gray-500">
              {simulationData?.name || 'Team Simulation'}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Title section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Waiting Room</h1>
          <p className="text-gray-600">
            {scheduledStartTime
              ? 'Please wait here until the simulation starts. Test your devices and meet your team members.'
              : 'Waiting for the host to start the simulation.'}
          </p>
        </div>

        {/* Connection error warning - shown when we have a transient API error but have loaded before */}
        {connectionError && (
          <div className="max-w-md mx-auto mb-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5 animate-pulse"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-orange-800">{connectionError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-start error message */}
        {autoStartError && (
          <div className="max-w-md mx-auto mb-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-yellow-800">{autoStartError}</p>
                <button
                  onClick={handleRetry}
                  className="mt-2 text-sm text-yellow-700 hover:text-yellow-900 underline"
                >
                  Retry now
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Left column: Device Preview */}
          <div>
            <DevicePreview />
          </div>

          {/* Right column: Countdown Timer & Participants */}
          <div className="space-y-6">
            {/* Countdown Timer (only if scheduled time exists) */}
            {scheduledStartTime && isSimulationPending && (
              <CountdownTimer
                targetTime={scheduledStartTime}
                timezone={simulationData?.timezone || undefined}
                onTimeUp={handleTimeUp}
              />
            )}

            {/* No scheduled time - show waiting message */}
            {!scheduledStartTime && isSimulationPending && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-gray-600">Waiting for host to start the simulation...</p>
              </div>
            )}

            {/* Participant List */}
            <ParticipantWaitList
              participants={participants}
              currentUserEmail={currentUserEmail}
            />
          </div>
        </div>

        {/* Help section */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <h3 className="font-semibold text-blue-900 mb-2">Need Help?</h3>
          <p className="text-sm text-blue-700 mb-3">
            Make sure your camera and microphone are working before the simulation starts.
            If you have any issues, try refreshing the page.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-blue-700 hover:text-blue-900 underline"
            >
              Refresh Page
            </button>
            <a
              href="mailto:support@meseekna.com"
              className="text-sm text-blue-700 hover:text-blue-900 underline"
            >
              Contact Support
            </a>
          </div>
        </div>
      </main>
    </div>
  )
}

export default CollaborativeSimulationSimple
